import React from 'react';
import { PCCase } from '../../types';
import { CaseOutline } from './CaseDrawing';
import { formatDimensionValue } from '../../utils/conversions';

const AREA_HEIGHT = 260; // px reserved for the tallest case
const MAX_SCALE = 0.5;   // px per mm
const MAX_CASES = 24;

interface ScaleViewProps {
  cases: PCCase[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  unit: 'mm' | 'in';
  canAddMore: boolean;
}

// All cases side by side, front view, on one shared scale with a height ruler behind them
export const ScaleView: React.FC<ScaleViewProps> = ({ cases, selectedIds, onToggle, unit, canAddMore }) => {
  const drawable = cases.filter((c) => c.dimensions?.height && c.dimensions?.width).slice(0, MAX_CASES);

  if (drawable.length === 0) {
    return null;
  }

  const tallest = Math.max(...drawable.map((c) => c.dimensions.height), 300);
  // Leave headroom above the tallest case for its height label and the top ruler label
  const scale = Math.min(MAX_SCALE, (AREA_HEIGHT - 24) / tallest);

  const stepMm = unit === 'mm' ? 100 : 4 * 25.4;
  const gridLines: { label: string; bottom: number }[] = [];
  for (let mm = stepMm; mm <= tallest; mm += stepMm) {
    gridLines.push({
      label: unit === 'mm' ? String(mm) : `${Math.round(mm / 25.4)}"`,
      bottom: Math.round(mm * scale),
    });
  }

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-line bg-surface px-4 pt-5 pb-4 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3 font-mono text-xs uppercase tracking-label text-muted">
        <span>Scale view · front · height in {unit}</span>
        <span className="flex flex-wrap items-center gap-4">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm border-[1.5px] border-fg" />
            Case
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm border-[1.5px] border-accent bg-accent-soft" />
            In comparison
          </span>
          <span className="normal-case tracking-normal">Click a case to add it</span>
        </span>
      </div>

      <div className="overflow-x-auto">
        <div className="relative min-w-max pl-12">
          {/* Height ruler */}
          <div className="pointer-events-none absolute inset-x-0 top-0" style={{ height: AREA_HEIGHT }} aria-hidden="true">
            {gridLines.map((line) => (
              <div key={line.label} className="absolute inset-x-0 border-t border-dashed border-line" style={{ bottom: line.bottom }}>
                <span className="absolute left-0 -top-2 bg-surface pr-1.5 font-mono text-2xs text-muted">{line.label}</span>
              </div>
            ))}
            <div className="absolute inset-x-0 bottom-0 border-t border-line-strong" />
          </div>

          <div className="relative flex gap-7 pr-3">
            {drawable.map((c) => {
              const selected = selectedIds.includes(c.id);
              const blocked = !selected && !canAddMore;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => !blocked && onToggle(c.id)}
                  aria-pressed={selected}
                  aria-disabled={blocked}
                  aria-label={`${selected ? 'Remove' : 'Add'} ${c.manufacturer} ${c.name} ${selected ? 'from' : 'to'} comparison`}
                  title={blocked ? 'Comparison is full (4 cases)' : `${c.manufacturer} ${c.name}`}
                  className={`group/sil flex flex-col items-center gap-2 rounded-md ${blocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className="flex flex-col items-center justify-end gap-1.5" style={{ height: AREA_HEIGHT }}>
                    <span className={`font-mono text-2xs ${selected ? 'text-accent-ink' : 'text-muted'}`}>
                      {formatDimensionValue(c.dimensions.height, unit)}
                    </span>
                    <CaseOutline
                      width={Math.max(6, Math.round(c.dimensions.width * scale))}
                      height={Math.round(c.dimensions.height * scale)}
                      selected={selected}
                      className={`${selected ? 'text-accent' : 'text-fg'} ${blocked ? '' : 'group-hover/sil:border-accent'}`}
                    />
                  </span>
                  <span className={`max-w-[96px] truncate text-xs font-medium ${selected ? 'text-accent-ink' : 'text-muted'}`}>
                    {c.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
